import { CircleCheckBig, Loader2 } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  useGetReportEvaluation,
  useStartReportEvaluation,
} from "~/api/custom-reports/custom-reports";
import { DemoCaseReferenceReport, ReportEvaluationPhase } from "~/api/model";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { downloadBlob } from "~/helpers";
import { trackEvent } from "~/utils/analytics";

const EVALUATION_POLL_INTERVAL_MS = 5_000;

const PHASE_MESSAGES: Partial<
  Record<ReportEvaluationPhase, { id: string; defaultMessage: string }>
> = {
  creating_template: {
    id: "cases.evaluate.phase.creatingTemplate",
    defaultMessage: "Generating template from sample…",
  },
  generating_report: {
    id: "cases.evaluate.phase.generatingReport",
    defaultMessage: "Generating report for the case…",
  },
  evaluating: {
    id: "cases.evaluate.phase.evaluating",
    defaultMessage: "Comparing with the sample…",
  },
  deleting_report: {
    id: "cases.evaluate.phase.deletingReport",
    defaultMessage: "Cleaning up…",
  },
  deleting_template: {
    id: "cases.evaluate.phase.deletingTemplate",
    defaultMessage: "Cleaning up…",
  },
};

const GENERIC_RUNNING_MESSAGE = {
  id: "cases.evaluate.running",
  defaultMessage: "Evaluation is running. This may take a moment.",
};

interface EvaluateCaseDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  organizationId: string;
  projectId: string;
  demoCaseId: string;
  referenceReport: DemoCaseReferenceReport;
  evaluationId?: string;
  setEvaluationId: (evaluationId?: string) => void;
}

type EvaluationMarkdownPayload = {
  evaluationId: string;
  evaluationMarkdown?: string;
};

const getEvaluationMarkdown = (
  evaluation: EvaluationMarkdownPayload
): string | undefined =>
  typeof evaluation.evaluationMarkdown === "string" &&
  evaluation.evaluationMarkdown.length > 0
    ? evaluation.evaluationMarkdown
    : undefined;

const getErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error) return error.message;
  return null;
};

const sanitizeSampleName = (filename: string): string => {
  const withoutExtension = filename.replace(/\.[^.]+$/, "");
  const sanitized = withoutExtension
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return sanitized || "sample";
};

const getRunningMessage = (phase: ReportEvaluationPhase | undefined) =>
  (phase && PHASE_MESSAGES[phase]) || GENERIC_RUNNING_MESSAGE;

export const EvaluateCaseDialog: FC<EvaluateCaseDialogProps> = ({
  open,
  setOpen,
  organizationId,
  projectId,
  demoCaseId,
  referenceReport,
  evaluationId,
  setEvaluationId,
}) => {
  const intl = useIntl();
  const [startError, setStartError] = useState<string | null>(null);
  const downloadedEvaluationIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (open) {
      setStartError(null);
    }
  }, [open, referenceReport.id]);

  useEffect(() => {
    downloadedEvaluationIdRef.current = null;
  }, [evaluationId]);

  const startEvaluation = useStartReportEvaluation();
  const {
    data: evaluation,
    error: evaluationError,
    refetch: refetchEvaluation,
  } = useGetReportEvaluation(organizationId, evaluationId ?? "", {
    query: {
      enabled: Boolean(evaluationId),
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return status === "pending" || status === "running"
          ? EVALUATION_POLL_INTERVAL_MS
          : false;
      },
      refetchIntervalInBackground: true,
    },
  });

  const isRunning =
    evaluation?.status === "pending" || evaluation?.status === "running";

  useEffect(() => {
    if (!evaluationId || !evaluation || evaluation.status !== "completed") {
      return;
    }
    if (downloadedEvaluationIdRef.current === evaluation.evaluationId) return;

    const markdown = getEvaluationMarkdown(evaluation);
    if (!markdown) return;

    downloadedEvaluationIdRef.current = evaluation.evaluationId;

    const dateStamp = new Date().toISOString().slice(0, 10);
    const filename = `eval-${sanitizeSampleName(referenceReport.filename)}-${dateStamp}.md`;
    downloadBlob(
      new Blob([markdown], { type: "text/markdown;charset=utf-8" }),
      filename
    );
  }, [evaluation, evaluationId, referenceReport.filename]);

  const handleStart = async () => {
    setStartError(null);
    try {
      const result = await startEvaluation.mutateAsync({
        organizationId,
        data: {
          caseId: projectId,
          demoCaseId,
          referenceReportId: referenceReport.id,
        },
      });
      setEvaluationId(result.evaluationId);
      trackEvent({
        eventName: "app/cases/evaluate_case.button/start",
      });
    } catch (error: unknown) {
      setStartError(
        getErrorMessage(error) ??
          intl.formatMessage({
            id: "cases.evaluate.startError",
            defaultMessage:
              "We couldn't start the evaluation. Please try again.",
          })
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id="cases.evaluate.title"
              defaultMessage="Evaluate Report"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="cases.evaluate.description"
              defaultMessage="Evaluate against {referenceReport}."
              values={{ referenceReport: referenceReport.filename }}
            />
          </DialogDescription>
        </DialogHeader>

        {!evaluationId && (
          <>
            {startError && (
              <p className="text-sm text-destructive" role="alert">
                {startError}
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              <Button
                onClick={handleStart}
                disabled={startEvaluation.isPending || isRunning}
              >
                {startEvaluation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                <FormattedMessage
                  id="cases.evaluate.start"
                  defaultMessage="Start Evaluation"
                />
              </Button>
            </DialogFooter>
          </>
        )}

        {evaluationId && evaluationError && (
          <div className="space-y-2 text-sm text-destructive" role="alert">
            <p>
              {getErrorMessage(evaluationError) ??
                intl.formatMessage({
                  id: "cases.evaluate.statusError",
                  defaultMessage:
                    "We couldn't load the evaluation status. The evaluation may still be running; try again.",
                })}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => void refetchEvaluation()}
              >
                <FormattedMessage
                  id="cases.evaluate.retry"
                  defaultMessage="Retry"
                />
              </Button>
              <Button
                variant="outline"
                onClick={() => setEvaluationId(undefined)}
              >
                <FormattedMessage
                  id="cases.evaluate.startNew"
                  defaultMessage="Start New Evaluation"
                />
              </Button>
            </div>
          </div>
        )}

        {evaluationId && !evaluation && !evaluationError && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <FormattedMessage
              id="cases.evaluate.loading"
              defaultMessage="Loading evaluation status…"
            />
          </div>
        )}

        {evaluation && (
          <div className="space-y-4">
            {isRunning && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <FormattedMessage {...getRunningMessage(evaluation.phase)} />
              </div>
            )}
            {evaluation.status === "failed" && (
              <div className="space-y-1 text-sm text-destructive" role="alert">
                <p className="font-medium">
                  <FormattedMessage
                    id="cases.evaluate.failed"
                    defaultMessage="Evaluation failed"
                  />
                </p>
                <p>
                  {evaluation.error ??
                    intl.formatMessage({
                      id: "cases.evaluate.failedReason",
                      defaultMessage:
                        "The report could not be compared with the reference report. Please try again.",
                    })}
                </p>
              </div>
            )}
            {evaluation.status === "completed" && (
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <CircleCheckBig className="h-8 w-8 text-green-500" />
                <p className="text-sm font-medium">
                  <FormattedMessage
                    id="cases.evaluate.completed"
                    defaultMessage="Evaluation completed successfully"
                  />
                </p>
                <p className="text-sm text-muted-foreground">
                  <FormattedMessage
                    id="cases.evaluate.downloadStarted"
                    defaultMessage="The evaluation file is downloading."
                  />
                </p>
              </div>
            )}
            {!isRunning && (
              <Button
                variant="outline"
                onClick={() => setEvaluationId(undefined)}
              >
                <FormattedMessage
                  id="cases.evaluate.startNew"
                  defaultMessage="Start New Evaluation"
                />
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
